import { HARVEST_HANDLE } from "./harvest/look.ts";
import type { InteractTarget } from "./interact-target.ts";

/**
 * What E does now, one answer for the key, the world prompt and the touch Interact button (ADR-061's amendment of
 * 2026-10-31, decision 12): `verb` acts on the thing the prompt names, `harvest` starts harvesting the prop in reach,
 * `door` asks the door the character stands on, `pickUp` takes the nearest ground item, `waiting` is a thing in reach
 * still finishing its move or a conversation in progress, where E says nothing, and `none` is nothing in reach.
 */
export type InteractKind = "verb" | "harvest" | "door" | "pickUp" | "waiting" | "none";

/**
 * The choice: its kind and, for `verb`, the contract verb the target offers.
 */
export interface InteractContext {
  readonly kind: InteractKind;
  readonly verb: string;
}

/**
 * An `InteractContext` written in place.
 */
export interface MutableInteractContext {
  kind: InteractKind;
  verb: string;
}

/**
 * A context to write into, holding nothing in reach.
 *
 * @returns The context.
 */
export function newInteractContext(): MutableInteractContext {
  return { kind: "none", verb: "" };
}

/**
 * Chooses what E acts on, writing the choice into `out` so a frame allocates nothing: the target the prompt names comes
 * first, unless the character stands on a door and the player has not explicitly targeted that thing (the door beats
 * harvesting and a thing nobody targeted); then the door; then the silent cases; then the nearest ground item.
 *
 * @param out Where the choice is written.
 * @param target What the prompt names.
 * @param atDoor Whether the character stands on a door that takes E.
 * @param doorSelected The handle the player has targeted, -1 for none.
 * @param groundInReach Whether a ground item is in reach.
 * @param talking Whether a conversation is open.
 */
export function resolveInteractInto(out: MutableInteractContext, target: InteractTarget, atDoor: boolean, doorSelected: number, groundInReach: boolean, talking: boolean): void {
  out.verb = "";
  if (target.found && (!atDoor || (target.handle !== HARVEST_HANDLE && target.handle === doorSelected))) {
    if (target.handle === HARVEST_HANDLE) {
      out.kind = "harvest";
    } else {
      out.kind = "verb";
      out.verb = target.verb;
    }
  } else if (atDoor) {
    out.kind = "door";
  } else if (target.waiting || talking) {
    out.kind = "waiting";
  } else {
    out.kind = groundInReach ? "pickUp" : "none";
  }
}
